import { IconButton, Menu, useMenu } from "@kaltura/ds-react-components";
import styled from "@emotion/styled";
import { CustomComponent } from "@kaltura/mediaspace-shared-ui";
import { KmsTypeComponent, SharedMenuItemProps } from "@kaltura/mediaspace-shared-types";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { MoreVertical24Icon } from "@kaltura/ds-react-icons";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";

export interface ActionsProps {
    menuItems: KmsTypeComponent<any, SharedMenuItemProps>[];
    entryId: string;
    entryName: string;

    /**
     * display items flat or in a collapsed menu
     */
    displayInline?: boolean;

    reloadMediaList?: () => void;

    /**
     * Function to update the cached results
     * Generic type parameter allows flexibility for different data structures
     */
    updateCachedResults?: <T>(updater: (oldData: T) => T) => void;
}

const StyledActions = styled("div")(({ theme }) => ({
    display: "flex",
    justifyContent: "flex-end",
    flexGrow: 1,
    [theme.breakpoints.down(theme.breakpoints.values.md)]: {
        flexGrow: 0,
    },
    columnGap: theme.spacing(1),
}));

export function Actions(props: ActionsProps) {
    const { menuItems, entryId, entryName, displayInline, reloadMediaList, updateCachedResults } = props;

    const sendButtonAnalytics = useButtonAnalytics();

    const { close, buttonProps, menuProps } = useMenu({
        menuId: "my-media-card-actions-menu",
        onOpen: () => sendButtonAnalytics("card - Media Actions", ButtonClickAnalyticsType.LINK, entryId),
    });

    if (displayInline) {
        return (
            <StyledActions className={"kms-ds-my-media-card-actions"}>
                {menuItems.map((component, index) => (
                    <CustomComponent
                        key={index}
                        component={component}
                        onCloseMenu={close}
                        reloadMedia={reloadMediaList}
                        updateCachedResults={updateCachedResults}
                    />
                ))}
            </StyledActions>
        );
    }

    return (
        <StyledActions className={"kms-ds-my-media-card-actions"}>
            <IconButton
                {...buttonProps}
                id={"my-media-card-actions-button"}
                variant={"borderless"}
                color={"secondary"}
                aria-label={translate("show actions for media: %1", [entryName])}
            >
                <MoreVertical24Icon />
            </IconButton>

            <Menu
                {...menuProps}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                aria-labelledby={"my-media-card-actions-button"}
            >
                {menuItems.map((component, index) => (
                    <CustomComponent
                        key={index}
                        component={component}
                        onCloseMenu={close}
                        reloadMedia={reloadMediaList}
                        updateCachedResults={updateCachedResults}
                        autoFocus={index === 0}
                    />
                ))}
            </Menu>
        </StyledActions>
    );
}

export default Actions;
